import toNumber from 'lodash/toNumber'
import toPairs from 'lodash/toPairs'
import values from 'lodash/values'

import { CalendarQuarter } from '@klear/date/calendar-quarter'
import { UPLOAD_INVALID_TYPE_MESSAGE, isUploadContentType } from '@klear/upload/upload-formats'
import {
  DOCUMENT_FILE_SIZE_EXCEEDED_MESSAGE,
  DOCUMENT_UPLOAD_MAX_BYTES,
  DOCUMENT_UPLOAD_MAX_FILES
} from '@klear/document/schema'

export type DocumentForm = {
  id?: number
  title: string
  url?: string
  notes?: string
  file?: File[]
  financialStatement?: boolean
  periodEndYear?: string
  periodEndQuarter?: string
}

export type DocumentFormErrorMap = Record<string, string | undefined>

export type DocumentFormValidateOptions = {
  requireFile?: boolean
}

function validateDocumentFiles(files: File[] | undefined, requireFile: boolean): string | undefined {
  if (!files || files.length === 0) {
    return requireFile ? 'No files selected' : undefined
  }
  if (files.length > DOCUMENT_UPLOAD_MAX_FILES) {
    return `Maximum ${DOCUMENT_UPLOAD_MAX_FILES} files allowed`
  }
  for (const file of files) {
    if (!isUploadContentType(file.type)) {
      return UPLOAD_INVALID_TYPE_MESSAGE
    }
    if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
      return DOCUMENT_FILE_SIZE_EXCEEDED_MESSAGE
    }
  }
  return undefined
}

export function validateDocumentForm(
  form: DocumentForm,
  options: DocumentFormValidateOptions = {}
): DocumentFormErrorMap {
  const errors: DocumentFormErrorMap = {}
  const requireFile = options.requireFile === true

  if (!requireFile && !form.title.trim()) {
    errors.title = 'Title is required'
  }

  const fileError = validateDocumentFiles(form.file, requireFile)
  if (fileError) {
    errors.file = fileError
  }

  if (form.financialStatement === true) {
    const periodEnd = documentFormPeriodEnd(form)
    if (periodEnd === null) {
      errors.periodEndQuarter = "Choose the year and quarter this statement's period ends in"
    } else if (!periodEnd.hasEnded()) {
      errors.periodEndQuarter = `${periodEnd.label} hasn't ended yet`
    }
  }

  return errors
}

export function documentFormPeriodEnd(form: DocumentForm): CalendarQuarter | null {
  if (form.financialStatement !== true || !form.periodEndYear || !form.periodEndQuarter) return null
  return CalendarQuarter.of({ year: toNumber(form.periodEndYear), quarter: toNumber(form.periodEndQuarter) })
}

export function hasDocumentFormErrors(errors: DocumentFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function documentFormFieldErrors(
  errors: DocumentFormErrorMap
): Record<string, { type: 'manual'; message: string }> {
  const fieldErrors: Record<string, { type: 'manual'; message: string }> = {}
  for (const [key, message] of toPairs(errors)) {
    if (message) {
      fieldErrors[key] = { type: 'manual', message }
    }
  }
  return fieldErrors
}
